import { useNavigate, useParams } from "react-router-dom"
import Layout from "../Frames/Layout"
import API from "../../services/API"
import Loader from "../Tools/Loader"
import { Alert, MenuItem, Stack, TextField } from "@mui/material"
import { useState } from "react"
import { useEffect } from "react"
import { useContext } from "react"
import UserContext from "../../services/Context/UserContext"
import ToastContext from "../../services/Context/ToastContext"
import Category from "../Inputs/Category"
import Picture from "../Inputs/Picture"
import Toggle from "../Inputs/Toggle"
import { LoadingButton } from "@mui/lab"
import Siret from "../Inputs/Siret"
import { Delete } from "@mui/icons-material"
import Top from "./Common/Top"
import { txt } from "../../services/translation"

// Académie du Sourire only, the API ignores this code for any other company
const QUEOVAL_SIRET = 49198279900034

export default function CourseForm(){

    const {id} = useParams()

    const navigate = useNavigate()

    const {user} = useContext(UserContext)
    const {setToast} = useContext(ToastContext)

    const [data, setData] = useState({})

    const [status, setStatus] = useState('loading')

    const statusIs = str => str === status
    const statusIn = arr => arr.includes(status)

    useEffect(() => {

        if(id !== 'new'){

            API.get(`/design/courses/${id}`)
                .then(r => {
                    setData(r.data)
                    setStatus('ready')
                })
                .catch(e => {
                    console.error(e?.response?.data)
                    setStatus('error')
                })
            
        }else{

            setData({
                type: {id: 'course'},
                category: user.category,
                siret: user.siret,
                published: false,
                private: false,
                forum: true,
                kind: {id: 'elearning'}
            })

            setStatus('ready')

        }

    }, [id, user])


    const handleSubmit = e => {
        e.preventDefault();

        setStatus('submit')

        save().then(r => handleSuccess(r))
            .catch(e => handleError(e))
            .finally(() => setStatus('ready'))
    }

    const save = async () => {

        if(id === 'new'){

            return await API.post(`/design/courses`, data);

        }else{

            return await API.put(`/design/courses/${id}`, data);
        }
    }

    const handleSuccess = r => {
        console.log(r.data)

        setToast({
            severity: 'success', 
            message: txt('modificationsSavedSuccessfully'), 
            duration: 3000
        })

        if(id === 'new') navigate(`/design/${r.data?.id}`);
    }

    const handleError = e => {
        setToast({
            severity: 'error', 
            message: e.response?.data?.error ||  txt('errorCheckForm'), 
            duration: 3000
        })
    }

    const handleDelete = () => {
        const ok = window.confirm(txt('confirmDeleteCourse'));

        if(!ok) return false;

        setStatus('delete');

        API.delete(`/design/courses/${data.id}`)
            .then(() => navigate('/courses'))
            .catch(() => {
                setStatus('ready');
                setToast({
                    severity: 'error', 
                    message: txt('courseDeletionFailed'), 
                    duration: 3000
                })
            })
    }

    const handlePictureChange = file => {

        setData({...data, picture: file})

        if(file){
            API.upload('/pictures', file) 
                .then(r => setData({...data, picture: r.data}))
                .catch(e => {
                    console.log(e?.response?.data)
                    setData({...data, picture: null})
                    setToast({severity: 'error', message: txt('imageTooHeavyOrWrongFormat')})
                })
        }
    }


    return <Layout menu='design' tab='form'>

        {statusIn(['ready','submit','delete']) && 

            <form onSubmit={handleSubmit}>

                <Stack spacing={3} sx={{maxWidth: 600, margin: 'auto'}}>

                    <Top title={txt('editCourseTitle')} />

                    {user.admin && 
                        <TextField
                            label={txt('uniqueId')}
                            value={data.id || ''}
                            onChange={e => setData({...data, id: 
                                e.target.value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase()
                            })}
                            required
                            disabled={id !== 'new'}
                            size='small'
                            inputProps={{min: 3, max: 36}}
                        />
                    }

                    <TextField
                        label={txt('courseName')}
                        value={data.title || ''}
                        onChange={e => setData({...data, title: 
                            e.target.value
                        })}
                        required
                        size='small'
                        inputProps={{maxLength: 200}}
                    />

                    {user.admin && 
                        <TextField
                            label={txt('courseType')}
                            value={data.type?.id || ''}
                            onChange={e => setData({...data, type: {id: e.target.value}})}
                            required
                            size='small'
                            select
                        >
                            <MenuItem value='course'>{txt('training')}</MenuItem>
                            <MenuItem value='tuto'>{txt('tutorial')}</MenuItem>
                        </TextField>
                    }


                    {user.admin && 
                        <Category 
                            value={data.category}
                            onChange={category => setData({...data, category})}
                            required
                        />
                    }

                    <TextField
                        label={txt('speakers')}
                        value={data.teachers || ''}
                        onChange={e => setData({...data, teachers: 
                            e.target.value
                        })}
                        size='small'
                        inputProps={{maxLength: 200}}
                    />

                    {user.admin && <>

                        <TextField
                            label="Type de formation"
                            value={data.kind?.id ?? 'elearning'}
                            onChange={e => setData({...data, kind: 
                                { id: e.target.value }
                            })}
                            size='small'
                            select
                        >
                            <MenuItem value="elearning">Formation classique</MenuItem>
                            <MenuItem value="masterclass">MasterClass</MenuItem>
                            <MenuItem value="learnyspeed">LearnySpeed</MenuItem>
                        </TextField>

                        {['learnyspeed', 'masterclass'].includes(data.kind?.id) &&
                            <TextField
                                label="Identifiant du produit Shopify"
                                value={data.shopifyId ?? ''}
                                onChange={e => setData({...data, shopifyId: 
                                    e.target.value ?? null
                                })}
                                size='small'
                                inputProps={{maxLength: 50}}
                            />
                        }
                        
                    </>}


                    {user.admin && <>

                        <TextField
                            label={txt('salesPageUrl')}
                            type="url"
                            value={data.link || ''}
                            onChange={e => setData({...data, link: e.target.value})}
                            size='small'
                            inputProps={{maxLength: 250}}
                        />

                        <TextField
                            label={txt('customEnrollUrl')}
                            type="url"
                            value={data.customEnrollUrl || ''}
                            onChange={e => setData({...data, customEnrollUrl: e.target.value})}
                            size='small'
                            inputProps={{maxLength: 250}}
                        />

                    </>}

                    {(user.admin || Number(data.siret) === QUEOVAL_SIRET) &&
                        <TextField
                            label="Code produit Queoval"
                            value={data.queovalId ?? ''}
                            onChange={e => setData({...data, queovalId: e.target.value || null})}
                            size='small'
                            inputProps={{maxLength: 100}}
                            helperText="Code produit des stages qui alimentent cette formation, à reprendre tel quel depuis Queoval. S'il porte le millésime, il est à mettre à jour chaque année."
                        />
                    }


                    <Stack spacing={1}>

                        <Picture 
                            id='course-picture'
                            label={txt('addThumbnail')}
                            value={data.picture}
                            onChange={handlePictureChange}
                        />

                        <Toggle 
                            label={txt('accessToCommunitySpace')}
                            value={data.forum}
                            onChange={(e,v) => setData({...data, forum: v})}
                        />

                        <Toggle 
                            label={txt('publishCourseInCatalog')}
                            value={data.published}
                            onChange={(e,v) => setData({...data, published: v})}
                        />

                        <Toggle 
                            label={txt('privateCourseForCompany')}
                            value={data.private}
                            onChange={(e,v) => setData({...data, private: v})}
                        />


                        {(user.admin && data.private) &&
                            <Siret 
                                value={data.siret || ''}
                                onChange={siret => setData({...data, siret})}
                                required
                            />
                        }
                    
                    </Stack>

                    <LoadingButton
                        type='submit'
                        variant='contained'
                        loading={statusIs('submit')}
                    >
                        {txt('save')}
                    </LoadingButton>


                    {id !== 'new' &&
                        <LoadingButton 
                            color='error'
                            startIcon={<Delete />} 
                            onClick={handleDelete}
                            loading={statusIs('delete')}
                            variant='outlined'
                            type='button'
                        >
                            {txt('deleteCourse')}
                        </LoadingButton>                    
                    }

                </Stack>

            </form>
        }

        {statusIs('loading') && 
            <Loader />
        }

        {statusIs('error') && 
            <Alert severity='error'>{txt('errorOccurred')}</Alert>
        }

    </Layout>
}